import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Check, X as XIcon, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import { normalizeSourceName, parseSourceUrl, getFaviconUrl } from '../../utils/leadPanelUtils';

interface ReasoningCriterion {
  criterion_text: string;
  reasoning_text: string;
  satisfied: 'yes' | 'no' | 'unclear';
  source_urls: (string | any)[];
}

interface MatchDetailsPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRect: DOMRect | null;
  companyName: string;
  companyUrl?: string;
  criterion: ReasoningCriterion | null;
  criterionIndex?: number;
  overallStatus?: 'match' | 'no-match' | 'partial';
  onViewAllCriteria?: () => void;
  totalCriteria?: number;
  reasoning?: Record<string, any>;
  onNavigate?: (index: number) => void;
}

// Add citation numbers to text and return formatted JSX
const formatTextWithCitations = (
  text: string,
  sources: any[],
  isExpanded: boolean,
  onCitationClick: (idx: number) => void
): React.ReactNode => {
  if (!text || sources.length === 0) {
    return isExpanded ? text : truncateText(text);
  }

  // Split into sentences
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
  const displaySentences = isExpanded ? sentences : sentences.slice(0, 2);

  // Distribute citations across sentences
  const citationsPerSentence = Math.ceil(sources.length / displaySentences.length);

  return (
    <>
      {displaySentences.map((sentence, sIdx) => {
        const startCitation = sIdx * citationsPerSentence;
        const endCitation = Math.min(startCitation + citationsPerSentence, sources.length);
        const citationsForSentence = sources.slice(startCitation, endCitation);

        return (
          <React.Fragment key={sIdx}>
            {sentence.trim()}
            {citationsForSentence.length > 0 && (
              <span className="inline-flex items-center gap-0.5 ml-0.5">
                {citationsForSentence.map((_, cIdx) => (
                  <button
                    key={startCitation + cIdx}
                    onClick={() => onCitationClick(startCitation + cIdx)}
                    className="inline-flex items-center justify-center w-4 h-4 text-[9px] font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                    title={`View source ${startCitation + cIdx + 1}`}
                  >
                    {startCitation + cIdx + 1}
                  </button>
                ))}
              </span>
            )}
            {sIdx < displaySentences.length - 1 && ' '}
          </React.Fragment>
        );
      })}
      {!isExpanded && sentences.length > 2 && '...'}
    </>
  );
};

const truncateText = (text: string): string => {
  if (!text) return '';
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
  if (sentences.length <= 2) return text;
  return sentences.slice(0, 2).join(' ').trim() + '...';
};

export const MatchDetailsPopover: React.FC<MatchDetailsPopoverProps> = ({
  isOpen,
  onClose,
  anchorRect,
  // companyName, onViewAllCriteria available via props but not used in current implementation
  criterion,
  criterionIndex = 1,
  totalCriteria = 1,
  reasoning = {},
  onNavigate,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const sourceRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [position, setPosition] = useState<{ top: number; left: number; placement: 'bottom' | 'top'; arrowLeft: number }>({ top: 0, left: 0, placement: 'bottom', arrowLeft: 170 });
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [activeSource, setActiveSource] = useState<number | null>(null);

  const allCriteria = Object.values(reasoning) as ReasoningCriterion[];
  const currentIndex = criterionIndex - 1;
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < allCriteria.length - 1;

  useEffect(() => {
    setIsExpanded(false);
    setActiveSource(null);
    sourceRefs.current = [];
    if (slideDirection) {
      const timer = setTimeout(() => setSlideDirection(null), 200);
      return () => clearTimeout(timer);
    }
  }, [criterionIndex, slideDirection]);

  const scrollToSource = useCallback((idx: number) => {
    setActiveSource(idx);
    const sourceEl = sourceRefs.current[idx];
    if (sourceEl) {
      sourceEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const navigateTo = useCallback((newIndex: number, direction: 'left' | 'right') => {
    if (newIndex >= 0 && newIndex < allCriteria.length) {
      setSlideDirection(direction);
      onNavigate?.(newIndex);
    }
  }, [allCriteria.length, onNavigate]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !anchorRect) return;
    const w = 340, h = 320, pad = 16, off = 8;
    let left = anchorRect.left + anchorRect.width / 2 - w / 2;
    let arrowLeft = w / 2;
    if (left < pad) { arrowLeft -= (pad - left); left = pad; }
    else if (left + w > window.innerWidth - pad) {
      const ov = left + w - (window.innerWidth - pad);
      arrowLeft += ov; left = window.innerWidth - w - pad;
    }
    arrowLeft = Math.max(24, Math.min(w - 24, arrowLeft));
    let top = anchorRect.bottom + off + 6;
    let placement: 'bottom' | 'top' = 'bottom';
    if (top + h > window.innerHeight - pad) {
      top = anchorRect.top - off - h - 6;
      placement = 'top';
      if (top < pad) { top = anchorRect.bottom + off + 6; placement = 'bottom'; }
    }
    setPosition({ top, left, placement, arrowLeft });
    requestAnimationFrame(() => setIsVisible(true));
  }, [isOpen, anchorRect]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) handleClose();
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 50);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handleClickOutside); };
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 150);
  };

  if (!isOpen || !anchorRect || !criterion) return null;

  const status = criterion.satisfied;
  const cfg = {
    yes: { bg: '#D1FAE5', color: '#059669', icon: Check, label: 'Match' },
    no: { bg: '#FEE2E2', color: '#DC2626', icon: XIcon, label: 'No Match' },
    unclear: { bg: '#FEF3C7', color: '#D97706', icon: Minus, label: 'Unclear' }
  }[status] || { bg: '#F3F4F6', color: '#6B7280', icon: Minus, label: 'Unknown' };

  const sources = (criterion.source_urls || []).map(parseSourceUrl).filter(Boolean).slice(0, 6);
  const hasMoreText = (criterion.reasoning_text?.match(/[^.!?]+[.!?]+/g) || []).length > 2;

  return (
    <>
      <style>{`
        @keyframes popIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes popOut { from { opacity: 1; } to { opacity: 0; transform: scale(0.97); } }
        @keyframes slideFromRight { from { opacity: 0; transform: translateX(12px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideFromLeft { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5); } 50% { box-shadow: 0 0 0 4px rgba(59, 130, 246, 0); } }
        .slide-left { animation: slideFromLeft 180ms ease-out; }
        .slide-right { animation: slideFromRight 180ms ease-out; }
        .source-highlight { animation: pulse 600ms ease-out; }
      `}</style>

      <div className="fixed inset-0 z-[299]" onMouseDown={(e) => { e.stopPropagation(); handleClose(); }} />

      {/* Arrow */}
      <div
        className="fixed z-[301] transition-opacity duration-150"
        style={{
          left: position.left + position.arrowLeft - 6,
          top: position.placement === 'bottom' ? position.top - 6 : position.top + (isExpanded ? 420 : 320),
          opacity: isVisible ? 1 : 0,
        }}
      >
        <svg width="12" height="6" viewBox="0 0 12 6" fill="none">
          <path d={position.placement === 'bottom' ? "M6 0L12 6H0L6 0Z" : "M6 6L0 0H12L6 6Z"} fill="white" />
        </svg>
      </div>

      <div
        ref={popoverRef}
        data-tour="match-popover"
        className="fixed z-[300] bg-white rounded-2xl flex flex-col"
        style={{
          top: position.top,
          left: position.left,
          width: 340,
          maxHeight: isExpanded ? 480 : 380,
          animation: isVisible ? 'popIn 200ms cubic-bezier(0.2, 0, 0, 1) forwards' : 'popOut 120ms ease-out forwards',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 10px 15px -3px rgba(0,0,0,0.1), 0 20px 25px -5px rgba(0,0,0,0.1)',
          transition: 'max-height 200ms ease',
        }}
      >
        {/* Header */}
        <div className="px-3 py-2.5 flex items-center justify-between border-b border-gray-50">
          <div className="flex items-center">
            {totalCriteria > 1 && (
              <>
                <button
                  onClick={() => canGoPrev && navigateTo(currentIndex - 1, 'left')}
                  className={`p-1 rounded-full transition-all ${canGoPrev ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:scale-95' : 'text-gray-200'}`}
                >
                  <ChevronLeft size={16} />
                </button>
                <div className="flex items-center gap-1 mx-1">
                  {Array.from({ length: totalCriteria }).map((_, i) => (
                    <div
                      key={i}
                      className={`rounded-full transition-all duration-200 ${i === currentIndex ? 'w-4 h-1.5 bg-gray-400' : 'w-1.5 h-1.5 bg-gray-200'}`}
                    />
                  ))}
                </div>
                <button
                  onClick={() => canGoNext && navigateTo(currentIndex + 1, 'right')}
                  className={`p-1 rounded-full transition-all ${canGoNext ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:scale-95' : 'text-gray-200'}`}
                >
                  <ChevronRight size={16} />
                </button>
              </>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-full text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-all active:scale-95"
          >
            <X size={14} />
          </button>
        </div>

        {/* Content */}
        <div
          className={`flex-1 overflow-y-auto px-4 py-3 ${slideDirection === 'left' ? 'slide-left' : slideDirection === 'right' ? 'slide-right' : ''}`}
          key={criterionIndex}
        >
          {/* Status Badge */}
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-2.5"
            style={{ backgroundColor: cfg.bg }}
          >
            <cfg.icon size={12} style={{ color: cfg.color }} strokeWidth={2.5} />
            <span className="text-[11px] font-semibold" style={{ color: cfg.color }}>
              {cfg.label}
            </span>
          </div>

          {/* Criterion */}
          <p className="text-[13px] font-medium text-gray-900 leading-snug mb-3">
            {criterion.criterion_text}
          </p>

          {/* Reasoning with inline citations */}
          <div className="mb-3">
            <p className="text-[12px] text-gray-600 leading-[1.7]">
              {formatTextWithCitations(
                criterion.reasoning_text,
                sources,
                isExpanded,
                (idx) => scrollToSource(idx)
              )}
            </p>
            {hasMoreText && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-2 text-[11px] font-medium text-blue-600 hover:text-blue-700 flex items-center gap-0.5 transition-colors"
              >
                {isExpanded ? <>Show less <ChevronUp size={12} /></> : <>Show more <ChevronDown size={12} /></>}
              </button>
            )}
          </div>

          {/* Sources - Enhanced list format */}
          {sources.length > 0 && (
            <div className="space-y-1.5 pt-3 border-t border-gray-100">
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">
                References
              </p>
              {sources.map((source: any, idx: number) => (
                <a
                  key={idx}
                  ref={(el) => { sourceRefs.current[idx] = el; }}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-2 p-1.5 -mx-1.5 rounded-lg transition-all hover:bg-gray-50 ${activeSource === idx ? 'bg-blue-50 source-highlight' : ''}`}
                  onMouseEnter={() => setActiveSource(idx)}
                  onMouseLeave={() => setActiveSource(null)}
                >
                  <span className="flex items-center justify-center w-4 h-4 text-[9px] font-semibold text-gray-400 bg-gray-100 rounded">
                    {idx + 1}
                  </span>
                  <img
                    src={getFaviconUrl(source.hostname, 32)}
                    alt=""
                    className="w-4 h-4 rounded"
                    onError={(e: any) => { e.target.style.opacity = '0.3'; }}
                  />
                  <span className="flex-1 text-[11px] text-gray-700 truncate">
                    {source.title || normalizeSourceName(source.hostname)}
                  </span>
                </a>
              ))}
              {(criterion.source_urls?.length || 0) > 6 && (
                <p className="text-[10px] text-gray-400 pl-6">
                  +{criterion.source_urls.length - 6} more sources
                </p>
              )}
            </div>
          )}
        </div>

      </div>
    </>
  );
};
